import React, { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { StepIndicator } from '../components/StepIndicator';
import { UploadIcon, ImageIcon, VideoIcon, CheckCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const STEPS = ['Master Creative', 'Campaign Setup', 'Select Creative', 'Styling Template', 'Variable Content', 'Generate', 'Review & Publish'];
const FORMATS = [{
  id: '1:1',
  name: 'Facebook Feed',
  ratio: '1:1 Square'
}, {
  id: '9:16',
  name: 'Instagram Story',
  ratio: '9:16 Portrait'
}, {
  id: '16:9',
  name: 'Google Display',
  ratio: '16:9 Landscape'
}, {
  id: '9:16-yt',
  name: 'YouTube Shorts',
  ratio: '9:16 Portrait'
}, {
  id: '4:5',
  name: 'Carousel',
  ratio: '4:5 Portrait'
}];
export function MasterCreativeEditing() {
  const navigate = useNavigate();
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'image' | 'video' | null>(null);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const type = file.type.startsWith('image/') ? 'image' : 'video';
      setFileType(type);
      setUploadedFile(URL.createObjectURL(file));
    }
  };
  const toggleFormat = (formatId: string) => {
    setSelectedFormats(prev => prev.includes(formatId) ? prev.filter(id => id !== formatId) : [...prev, formatId]);
  };
  const handleResize = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setIsComplete(true);
    }, 2000);
  };
  const handleContinue = () => {
    navigate('/dealerization/step-1');
  };
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-1947">
      <Sidebar data-id="element-1948" />
      <main className="flex-1" data-id="element-1949">
        <Header data-id="element-1950" />
        <StepIndicator currentStep={1} steps={STEPS} data-id="element-1951" />
        <div className="px-6 py-8" data-id="element-1952">
          <div className="max-w-4xl mx-auto" data-id="element-1953">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-1954">
              Step 0: Master Creative Editing
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-1955">
              Upload your master creative and AI will resize it for multiple
              formats
            </p>

            {!uploadedFile ? <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-12 text-center" data-id="element-1956">
                <UploadIcon className="mx-auto text-gray-400 mb-4" size={48} data-id="element-1957" />
                <h3 className="text-lg font-medium text-gray-700 mb-2" data-id="element-1958">
                  Upload Master Creative
                </h3>
                <p className="text-sm text-gray-500 mb-4" data-id="element-1959">
                  Support for images (JPG, PNG) and videos (MP4, MOV)
                </p>
                <label className="inline-block bg-blue-500 text-white px-6 py-2 rounded-md cursor-pointer hover:bg-blue-600" data-id="element-1960">
                  Choose File
                  <input type="file" className="hidden" accept="image/*,video/*" onChange={handleFileUpload} data-id="element-1961" />
                </label>
              </div> : <div className="space-y-6" data-id="element-1962">
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1963">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-1964">
                    Uploaded Creative
                  </h3>
                  <div className="flex items-center space-x-4" data-id="element-1965">
                    <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-1966">
                      {fileType === 'image' ? <ImageIcon className="text-gray-400" size={48} data-id="element-1967" /> : <VideoIcon className="text-gray-400" size={48} data-id="element-1968" />}
                    </div>
                    <div data-id="element-1969">
                      <p className="font-medium text-gray-700" data-id="element-1970">
                        {fileType === 'image' ? 'Master Image' : 'Master Video'}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1971">
                        Original dimensions detected
                      </p>
                      <button onClick={() => {
                    setUploadedFile(null);
                    setFileType(null);
                    setSelectedFormats([]);
                    setIsComplete(false);
                  }} className="text-sm text-blue-500 mt-2" data-id="element-1972">
                        Change file
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1973">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-1974">
                    Select Target Formats
                  </h3>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1975">
                    {FORMATS.map(format => <button key={format.id} onClick={() => toggleFormat(format.id)} className={`p-4 rounded-lg border-2 text-left transition-all ${selectedFormats.includes(format.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1976">
                        <div className="flex items-center justify-between mb-2" data-id="element-1977">
                          <span className="font-medium text-gray-800" data-id="element-1978">
                            {format.name}
                          </span>
                          {selectedFormats.includes(format.id) && <CheckCircleIcon className="text-blue-500" size={20} data-id="element-1979" />}
                        </div>
                        <span className="text-sm text-gray-500" data-id="element-1980">
                          {format.ratio}
                        </span>
                      </button>)}
                  </div>
                </div>

                {selectedFormats.length > 0 && !isComplete && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1981">
                    <h4 className="font-medium text-blue-900 mb-2" data-id="element-1982">
                      AI Resizing Features
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1" data-id="element-1983">
                      <li data-id="element-1984">
                        • <strong data-id="element-1985">Shrink:</strong> AI crops intelligently,
                        maintains focal points
                      </li>
                      <li data-id="element-1986">
                        • <strong data-id="element-1987">Expand:</strong> Adds blurred background to
                        fill aspect ratio
                      </li>
                      <li data-id="element-1988">
                        • <strong data-id="element-1989">Video:</strong> Subject tracking + smart crop
                        with audio preservation
                      </li>
                    </ul>
                  </div>}

                {isComplete && <div className="bg-green-50 border border-green-200 rounded-lg p-6" data-id="element-1990">
                    <div className="flex items-center mb-4" data-id="element-1991">
                      <CheckCircleIcon className="text-green-500 mr-3" size={24} data-id="element-1992" />
                      <div data-id="element-1993">
                        <h4 className="font-medium text-green-900" data-id="element-1994">
                          Resizing Complete!
                        </h4>
                        <p className="text-sm text-green-700" data-id="element-1995">
                          {selectedFormats.length} format variations created and
                          saved to library
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3" data-id="element-1996">
                      {selectedFormats.map(formatId => {
                  const format = FORMATS.find(f => f.id === formatId);
                  return <div key={formatId} className="bg-white rounded-lg border border-green-300 p-3" data-id="element-1997">
                            <div className="w-full h-24 bg-gray-100 rounded mb-2 flex items-center justify-center" data-id="element-1998">
                              {fileType === 'image' ? <ImageIcon className="text-gray-400" size={32} data-id="element-1999" /> : <VideoIcon className="text-gray-400" size={32} data-id="element-2000" />}
                            </div>
                            <p className="text-xs font-medium text-gray-700" data-id="element-2001">
                              {format?.name}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-2002">
                              {format?.ratio}
                            </p>
                          </div>;
                })}
                    </div>
                  </div>}

                <div className="flex justify-between" data-id="element-2003">
                  <button onClick={() => navigate('/dealerization')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2004">
                    Cancel
                  </button>
                  {!isComplete ? <button onClick={handleResize} disabled={selectedFormats.length === 0 || isProcessing} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-2005">
                      {isProcessing ? 'Processing...' : 'Resize with AI'}
                    </button> : <button onClick={handleContinue} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600" data-id="element-2006">
                      Continue to Campaign Setup
                    </button>}
                </div>
              </div>}
          </div>
        </div>
      </main>
    </div>;
}